import React, { FC, useEffect, useState, useCallback, useMemo } from "react";
import { Button, Spin, message, Steps, Modal, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import styles from "./styles.module.less";
import Axios from "axios";
import { VerifyCodeInput } from "../../../components/VerifyCodeInput";
import { getImgSrc, getOS } from "../../../utils";
import { useTranslation } from "react-i18next";
import { IconFont } from "@/components/IconFont";
import { loginSuccessRedirect } from "@/utils/loginSuccess";
import querystring from "querystring";
import { User } from "authing-js-sdk";
import { useAsyncFn } from "react-use";
import GoogleApple from "../../../assets/image/GoogleAuthenticator-Apple.png";
import GoogleAuthing from "../../../assets/image/GoogleAuthenticator-Authing.png";
import MicrosoftApple from "../../../assets/image/MicrosoftAuthenticator-Apple.png";
import MicrosoftBaidu from "../../../assets/image/MicrosoftAuthenticator-Baidu.png";
import { ErrorCodes, DOWNLOAD_AUTHENTICATOR_CONFIG } from "@/constants/enum";
import { useNavigate } from "react-router";
import { ConfigCard } from "@/components/ConfigCard";
import { useMediaSize } from "@/hooks/useMediaSize";
import googleLogo from "@/assets/image/logo-google@2x.png";
import microsoftLogo from "@/assets/image/logo-microsoft@2x.png";
import CopyIcon from "@/components/CopyIcon";
import { MobileSteps } from "@/components/MobileSteps";
import { useAuthingTranslation } from "@/hooks/useAuthingTranslation";
import classNames from "classnames";
import { i18n } from "@/locales";

const { Paragraph } = Typography;

export enum TotpSource {
  SELF = "SELF",
  APPLICATION = "APPLICATION",
}

const { Step } = Steps;

const MediaAppDownload: FC = () => {
  const { t } = useTranslation();
  const os = getOS();

  const appDownloadConfig = [
    {
      name: "Google Authenticator",
      imageUrl: googleLogo,
      download: () => {
        if (os && Object.keys(DOWNLOAD_AUTHENTICATOR_CONFIG).includes(os)) {
          // @ts-ignore
          window.open(`${DOWNLOAD_AUTHENTICATOR_CONFIG?.[os as never].google}`);
        }
      },
    },
    {
      name: "Microsoft Authenticator",
      imageUrl: microsoftLogo,
      download: () => {
        if (os && Object.keys(DOWNLOAD_AUTHENTICATOR_CONFIG).includes(os)) {
          window.open(
            // @ts-ignore
            `${DOWNLOAD_AUTHENTICATOR_CONFIG?.[os as never].microsoft}`
          );
        }
      },
    },
  ];

  return (
    <div className={styles.mediaAppDownload}>
      {appDownloadConfig.map((item, index) => (
        <div className={styles.mediaAppDownloadItem} key={index}>
          <div className={styles.itemHeader}>
            <img src={item.imageUrl} alt="app logo" />
            <span className="authing-mfa-dowload-item-title">{item.name}</span>
            <span
              className={`authing-mfa-dowload-item-description-${i18n.language}`}
            ></span>
          </div>
          <Button
            className={classNames(
              styles.btnItem,
              `authing-mfa-dowload-item-button-${i18n.language}`
            )}
            size="large"
            type="primary"
            onClick={item.download}
          >
            {t("common.download")}
          </Button>
        </div>
      ))}
    </div>
  );
};

const AppDownload: FC = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.appDownload}>
      <div className={styles.item}>
        <div
          className={styles.logo}
          style={{
            background: "#EFF2F6",
          }}
        >
          <IconFont
            type="authing-apple-web"
            style={{
              fontSize: 30,
              color: "#293350",
            }}
          />
        </div>
        <span className={styles.title}>{t("common.downloadTotpAppIOS")}</span>
        <span className={styles.subtitle}>
          {t("common.downloadTotpAppDocs")}
        </span>
        <div className={styles.QRCode}>
          <div className={styles.QRCodeItem}>
            <div
              className={styles.image}
              style={{
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundImage: `url('${GoogleApple}')`,
              }}
            />
            <span className={styles.QRCodeText}>Google Authenticator</span>
          </div>
          <div className={styles.QRCodeItem}>
            <div
              className={styles.image}
              style={{
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundImage: `url('${MicrosoftApple}')`,
              }}
            />
            <span className={styles.QRCodeText}>Microsoft Authenticator</span>
          </div>
        </div>
      </div>
      <div className={styles.item}>
        <div
          className={styles.logo}
          style={{
            background: "#28B1B0",
          }}
        >
          <IconFont
            type="authing-android"
            style={{
              fontSize: 30,
              color: "#FFF",
            }}
          />
        </div>
        <span className={styles.title}>
          {t("common.downloadTotpAppAndroid")}
        </span>
        <span className={styles.subtitle}>
          {t("common.downloadTotpAppDocs")}
        </span>
        <div className={styles.QRCode}>
          <div className={styles.QRCodeItem}>
            <div
              className={styles.image}
              style={{
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundImage: `url('${GoogleAuthing}')`,
              }}
            />
            <span className={styles.QRCodeText}>Google Authenticator</span>
          </div>
          <div className={styles.QRCodeItem}>
            <div
              className={styles.image}
              style={{
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundImage: `url('${MicrosoftBaidu}')`,
              }}
            />
            <span className={styles.QRCodeText}>Microsoft Authenticator</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScanQrcode: FC<{ qrcode: string; secret: string }> = ({
  qrcode,
  secret,
}) => {
  const { t } = useTranslation();
  const { at } = useAuthingTranslation();

  const { isPhoneMedia } = useMediaSize();

  const [userpoolName] = useState(window.__config__?.userpoolName);

  const desc = useMemo(
    () =>
      isPhoneMedia
        ? at("user.pleaseDownloadAuthenticatorMedia")
        : at("user.pleaseDownloadAuthenticator"),
    [at, isPhoneMedia]
  );

  return (
    <div className={styles.ScanQrcode}>
      <h4 className={styles.subtitle}>{t("user.scanQrcode")}</h4>
      <p className={styles.desc}>{desc}</p>

      {!isPhoneMedia ? (
        <img className={styles.qrcode} src={qrcode} alt="qrcode" />
      ) : (
        <>
          <div className={styles.secretInput}>
            <div className={styles.iconBox}>
              <IconFont type="authing-key-2-line" />
            </div>
            {t("common. appellation")}: {userpoolName}
            <CopyIcon
              copyValue={userpoolName!}
              style={{ marginLeft: "auto" }}
            />
          </div>
          <div className={styles.secretInput}>
            <div className={styles.iconBox}>
              <IconFont type="authing-key-2-line" />
            </div>
            {t("common.secret")}: {secret}
            <CopyIcon copyValue={secret} style={{ marginLeft: "auto" }} />
          </div>
        </>
      )}
    </div>
  );
};

export const InputSaftyCode: FC<{
  saftyCode: string[];
  setSaftyCode: (code: string[]) => void;
}> = ({ saftyCode, setSaftyCode }) => {
  const { t } = useTranslation();

  const { isPhoneMedia } = useMediaSize();

  return (
    <div>
      <h4 className={styles.subtitle}>{t("user.inputSafteyCode")}</h4>
      <p className={styles.desc}>{t("user.viewInputSixCode")}</p>

      <VerifyCodeInput
        className={styles.saftyCodeInput}
        verifyCode={saftyCode}
        setVerifyCode={setSaftyCode}
        gutter={isPhoneMedia ? "8px" : "30px"}
      />

      <p className={styles.saftyCodeTip}>{t("user.numberSafteyCode")}</p>
    </div>
  );
};

const SaveSecretKey: FC<{
  setIsSaved: Function;
  isSaved: boolean;
  secret: string;
}> = ({ secret, isSaved, setIsSaved }) => {
  const { t } = useTranslation();
  const { at } = useAuthingTranslation();
  const handleChange = (val: boolean) => {
    setIsSaved(val);
  };
  return (
    <div>
      <h4 className={styles.subtitle}>{t("user.saveSecret")}</h4>
      <p className={styles.desc}>{at("user.resetCodeDoWhat")}</p>

      <Paragraph className={styles.secretParagraph} copyable>
        {secret}
      </Paragraph>

      <Checkbox
        onChange={(evt) => handleChange(evt.target.checked)}
        checked={isSaved}
      >
        {t("login.rememberedSecret")}
      </Checkbox>
    </div>
  );
};

const BindSuccess: FC<{
  totpSource?: TotpSource;
  user?: User;
}> = ({ totpSource = TotpSource.SELF, user }) => {
  const { t } = useTranslation();
  const { at } = useAuthingTranslation();

  const [onBind, onBindFn] = useAsyncFn(async () => {
    if (user && totpSource === TotpSource.APPLICATION) {
      await window.__authing__.setCurrentUser(user);

      setTimeout(() => {
        loginSuccessRedirect(user);
      }, 1000);
    }
  }, [totpSource, user]);

  useEffect(() => {
    onBindFn();
  }, [onBindFn]);
  return (
    <Spin spinning={onBind.loading}>
      <div>
        <img
          width="148"
          height="148"
          style={{
            marginBottom: "34px",
          }}
          src={getImgSrc("/mfa-bind-success.png")}
          alt="bind success"
        />
        <h4 className={styles.subtitle}>{t("user.bindSuccess")}</h4>
        {totpSource === TotpSource.APPLICATION ? (
          <p className={styles.desc}>{t("common.bindAppTotp")}</p>
        ) : (
          <p className={styles.desc}>{at("user.bindedMfa")}</p>
        )}
      </div>
    </Spin>
  );
};

export interface UserMfaProps {
  totpSource?: TotpSource;
  MFAToken?: string;
  className?: string;
}

export const UserMfa: FC<UserMfaProps> = ({
  totpSource = TotpSource.SELF,
  MFAToken,
  className,
}) => {
  const getInitSaftyCode = () => new Array(6).fill("");
  const [saftyCode, setSaftyCode] = useState<string[]>(getInitSaftyCode());
  const [isSaved, setIsSaved] = useState(false);
  const [secret, setSecret] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [qrcode, setQrcode] = useState("");
  const [user, setUser] = useState<User>();
  const [btnLoading, setBtnLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const navigate = useNavigate();

  const { isPhoneMedia } = useMediaSize();

  const { t } = useTranslation();

  const { at } = useAuthingTranslation();

  const handleBind = async () => {
    setBtnLoading(true);
    try {
      if (totpSource === TotpSource.SELF) {
        await bindSelfTotp();
      } else {
        await bindApplcationTotp();
      }
    } catch (e) {
      message.error(t("user.bindFail"));
    } finally {
      setBtnLoading(false);
    }
  };

  const bindApplcationTotp = async () => {
    const data: any = await Axios.post(
      "/api/v2/mfa/totp/associate/confirm",
      {
        authenticator_type: "totp",
        totp: saftyCode.join(""),
        source: totpSource,
      },
      {
        headers: {
          authorization: MFAToken,
        },
      }
    );

    if (data.code === ErrorCodes.MAF_TOKEN_INVALID) {
      const query = Object.assign(querystring.parse(window.location.search));
      delete query.mfa_token;
      Modal.confirm({
        title: t("user.mfaInvalid"),
        content: t("user.mfaInvalidContent"),
        okText: t("user.backLogin"),
        async onOk() {
          navigate({
            pathname: "/login?",
            search: querystring.stringify(query),
          });
        },
      });
    } else if (data.code !== 200) {
      message.error(data.message);
    } else {
      const user = data?.data;
      message.success(t("user.bindSuccess"));
      setCurrentStep((currentStep + 1) as keyof typeof STEP_MAP);
      setSaftyCode(getInitSaftyCode());
      setBtnLoading(false);

      user && setUser(user);
    }
  };

  const bindSelfTotp = async () => {
    const data: any = await Axios.post("/api/v2/mfa/totp/associate/confirm", {
      authenticator_type: "totp",
      totp: saftyCode.join(""),
      source: totpSource,
    });

    if (data.code !== 200) {
      message.error(data.message);
    } else {
      message.success(t("user.bindSuccess"));
      setCurrentStep((currentStep + 1) as keyof typeof STEP_MAP);
      setSaftyCode(getInitSaftyCode());
    }
    setBtnLoading(false);
  };

  const handleUnbind = async () => {
    Modal.confirm({
      title: at("user.unbindMfa"),
      icon: <ExclamationCircleOutlined />,
      content: at("user.confirmUnbindMfa"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      async onOk() {
        setCanceling(true);
        try {
          const data: any = await Axios.delete("/api/v2/mfa/totp/associate");

          if (data.code !== 200) {
            message.error(data.message);
          } else {
            message.success(t("user.unbindSuccess"));
            fetchBindInfo();
            setCurrentStep(0);
          }
          setCanceling(false);
        } catch (e) {
          message.error(t("user.unbindFail"));
          setCanceling(false);
        }
      },
    });
  };

  const fetchBindInfo = useCallback(async () => {
    setIsSpinning(true);
    try {
      const config: {
        headers?: any;
      } = {};
      if (totpSource === TotpSource.APPLICATION) {
        config.headers = {
          authorization: MFAToken,
        };
      }
      const data: any = await Axios.post(
        "/api/v2/mfa/totp/associate",
        {
          authenticator_type: "totp",
          source: totpSource,
        },
        config
      );
      setSecret(data.data.recovery_code);
      setQrcode(data.data.qrcode_data_url);
      setMfaSecret(data.data.secret);
      setIsSpinning(false);
    } catch (e) {
      message.error(t("user.bindInfoFetchFail"));
      setIsSpinning(false);
    }
  }, [MFAToken, t, totpSource]);

  useEffect(() => {
    (async () => {
      setIsSpinning(true);
      try {
        const config: {
          headers?: any;
        } = {};
        if (totpSource === TotpSource.APPLICATION) {
          config.headers = {
            authorization: MFAToken,
          };
        }
        const query = {
          type: "totp",
          source: totpSource,
        };
        const data: any = await Axios.get(
          `/api/v2/mfa/authenticator?${querystring.stringify(query)}`,
          config
        );
        if (data.data.some((item: any) => item.enable)) {
          setCurrentStep(4);
          setIsSpinning(false);
          return;
        }
        fetchBindInfo();
      } catch (e) {
        setIsSpinning(false);
      }
    })();
  }, [MFAToken, fetchBindInfo, totpSource]);

  const STEP_MAP = useMemo(
    () => ({
      0: !isPhoneMedia ? <AppDownload /> : <MediaAppDownload />,
      1: <ScanQrcode qrcode={qrcode} secret={mfaSecret} />,
      2: <InputSaftyCode {...{ saftyCode, setSaftyCode }} />,
      3: <SaveSecretKey {...{ secret, setIsSaved, isSaved }} />,
      4: <BindSuccess totpSource={totpSource} user={user} />,
    }),
    [
      isPhoneMedia,
      isSaved,
      mfaSecret,
      qrcode,
      saftyCode,
      secret,
      totpSource,
      user,
    ]
  );

  const DISABLE_MAP = {
    0: () => false,
    1: () => false,
    2: () => saftyCode.some((item) => !item),
    3: () => !isSaved,
    4: () => false,
  };

  const [currentStep, setCurrentStep] = useState<keyof typeof STEP_MAP>(0);
  const handleNextStep = async () => {
    if (currentStep === 2) {
      await handleBind();
    } else {
      setCurrentStep((currentStep + 1) as keyof typeof STEP_MAP);
    }
  };
  const handlePrevStep = async () => {
    setCurrentStep((currentStep - 1) as keyof typeof STEP_MAP);
  };

  const useSteps = useMemo(
    () =>
      isPhoneMedia ? (
        <MobileSteps
          current={currentStep}
          data={[
            t("common.downloadTotpApp"),
            at("user.addMfa"),
            t("user.inputSafteyCode"),
            t("user.saveRecoverCode"),
            t("user.bindFinish"),
          ]}
        />
      ) : (
        <Steps size="small" current={currentStep}>
          <Step title={`${t("common.step")} 1`} />
          <Step title={`${t("common.step")} 2`} />
          <Step title={`${t("common.step")} 3`} />
          <Step title={`${t("common.step")} 4`} />
          <Step title={`${t("common.step")} 5`} />
        </Steps>
      ),
    [at, currentStep, isPhoneMedia, t]
  );
  return (
    <ConfigCard
      title={at("user.mfaBind")}
      className={className}
      extra={
        currentStep === 4 &&
        totpSource !== TotpSource.APPLICATION && (
          <Button
            className={styles.btnItem}
            type="link"
            size={"large"}
            loading={canceling}
            onClick={handleUnbind}
          >
            {t("login.releaseBind")}
          </Button>
        )
      }
    >
      <Spin spinning={isSpinning}>
        <div className={styles.mfaWrapper}>
          <div className={styles.mainContent}>
            {useSteps}

            <div className={styles.stepContent}>{STEP_MAP[currentStep]}</div>

            {(currentStep === 1 || currentStep === 2) && (
              <Button
                className={styles.btnItem}
                loading={btnLoading}
                size="large"
                onClick={handlePrevStep}
              >
                {t("user.prevStep")}
              </Button>
            )}
            {currentStep !== 4 && (
              <Button
                className={styles.nextItem}
                loading={btnLoading}
                disabled={DISABLE_MAP[currentStep]()}
                size="large"
                type="primary"
                onClick={handleNextStep}
              >
                {t("user.nextStep")}
              </Button>
            )}
          </div>
        </div>
      </Spin>
    </ConfigCard>
  );
};
